<p-dialog
  [visible]="deleteClientModal()"
  [modal]="true"
  [style]="{ width: '30vw' }"
  [breakpoints]="{ '960px': '75vw', '640px': '90vw' }"
  [draggable]="true"
  [resizable]="true"
  appendTo="body"
  (visibleChange)="onDialogVisibleChange($event)"
  (onHide)="closeDialogEvent()"
  aria-label="delete-client-dialog"
>
  <ng-template pTemplate="header">
    <div class="flex align-items-center gap-2">
      <i class="fa fa-trash text-xl text-red-500" aria-hidden="true"></i>
      <span class="font-semibold text-lg">
        {{ 'CLIENT_PROFILE.DELETE_CLIENT.TITLE' | translate }}
      </span>
    </div>
  </ng-template>

  <div class="p-3">
    <!-- Client Info -->
    <div
      class="flex align-items-center gap-3 mb-4 p-3 surface-100 border-round"
    >
      <p-avatar
        [label]="allClientData()?.full_name | shortName"
        [image]="allClientData()?.image"
        shape="circle"
        size="large"
        styleClass="text-lg"
        aria-label="client-avatar"
      />
      <div>
        <div class="font-semibold text-900">
          {{ allClientData()?.full_name }}
        </div>
        <div class="text-sm text-600">{{ allClientData()?.email_id }}</div>
      </div>
    </div>

    <!-- Pending Request Information -->
    @if (hasPendingRequest() && pendingDeletionRequest()) {
      <div class="mb-4">
        <div
          class="p-3 bg-yellow-50 border-round border-left-3 border-yellow-500 mb-3"
        >
          <div class="flex align-items-start gap-2">
            <i class="fa fa-clock text-yellow-600 mt-1" aria-hidden="true"></i>
            <div class="text-sm text-700">
              <div class="font-semibold mb-2">
                {{
                  'CLIENT_PROFILE.DELETE_CLIENT.PENDING_REQUEST_TITLE'
                    | translate
                }}
              </div>
              <div class="mb-1">
                <strong
                  >{{
                    'CLIENT_PROFILE.DELETE_CLIENT.REQUESTED_DATE' | translate
                  }}:</strong
                >
                {{ pendingDeletionRequest()?.requested_date | date: 'medium' }}
              </div>
              @if (pendingDeletionRequest()?.reason) {
                <div>
                  <strong
                    >{{
                      'CLIENT_PROFILE.DELETE_CLIENT.REASON' | translate
                    }}:</strong
                  >
                  {{ pendingDeletionRequest()?.reason }}
                </div>
              }
            </div>
          </div>
        </div>
        <div class="p-3 surface-100 border-round border-left-3 border-primary">
          <div class="flex align-items-start gap-2">
            <i
              class="fa fa-info-circle text-primary mt-1"
              aria-hidden="true"
            ></i>
            <div class="text-sm text-700">
              {{
                'CLIENT_PROFILE.DELETE_CLIENT.PENDING_INFO_MESSAGE' | translate
              }}
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Delete Form (only show if no pending request) -->
    @if (!hasPendingRequest()) {
      <form [formGroup]="deleteClientFormGroup">
        <!-- Warning Message -->
        <div
          class="mb-4 p-3 bg-red-50 border-round border-left-3 border-red-500"
        >
          <div class="flex align-items-start gap-2">
            <i
              class="fa fa-exclamation-triangle text-red-500 mt-1"
              aria-hidden="true"
            ></i>
            <div class="text-sm text-700">
              {{ 'CLIENT_PROFILE.DELETE_CLIENT.WARNING_MESSAGE' | translate }}
            </div>
          </div>
        </div>
        <!-- Reason Textarea -->
        <div class="field mb-4">
          <label class="font-medium text-700 text-sm mb-2 block">
            {{ 'CLIENT_PROFILE.DELETE_CLIENT.REASON_LABEL' | translate }}
            <span class="text-red-400">*</span>
          </label>
          <textarea
            pInputTextarea
            formControlName="reason"
            rows="3"
            class="w-full"
            placeholder="{{
              'CLIENT_PROFILE.DELETE_CLIENT.REASON_PLACEHOLDER' | translate
            }}"
            aria-label="reason-textarea"
            data-testid="reason-textarea"
          ></textarea>
          @if (Reason.touched && Reason.invalid) {
            <app-error-msg
              [errors]="Reason.errors"
              [fieldLabel]="
                'CLIENT_PROFILE.DELETE_CLIENT.REASON_LABEL' | translate
              "
              data-testid="reason-error"
            />
          }
        </div>
        <!-- Confirmation Text Input -->
        <div class="field mb-4">
          <label class="font-medium text-700 text-sm mb-2 block">
            {{ 'CLIENT_PROFILE.DELETE_CLIENT.CONFIRMATION_LABEL' | translate }}
            <span class="text-red-400">*</span>
          </label>
          <p class="text-sm text-600 mb-2">
            {{
              'CLIENT_PROFILE.DELETE_CLIENT.CONFIRMATION_INSTRUCTION'
                | translate: { clientName: allClientData()?.full_name }
            }}
          </p>
          <input
            pInputText
            type="text"
            formControlName="confirmationText"
            class="w-full"
            placeholder="{{
              'CLIENT_PROFILE.DELETE_CLIENT.CONFIRMATION_PLACEHOLDER'
                | translate
            }}"
            aria-label="confirmation-text-input"
            data-testid="confirmation-text-input"
          />
          @if (ConfirmationText.touched && ConfirmationText.invalid) {
            <app-error-msg
              [errors]="ConfirmationText.errors"
              [fieldLabel]="
                'CLIENT_PROFILE.DELETE_CLIENT.CONFIRMATION_LABEL' | translate
              "
              data-testid="confirmation-text-error"
            />
          }
        </div>
      </form>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-content-end gap-2">
      <button
        pButton
        pRipple
        type="button"
        [label]="
          hasPendingRequest()
            ? ('CLIENT_PROFILE.CLOSE' | translate)
            : ('CLIENT_PROFILE.CANCEL' | translate)
        "
        [class]="
          hasPendingRequest()
            ? 'p-button-primary'
            : 'p-button-outlined p-button-secondary'
        "
        (click)="closeDialogEvent()"
        [disabled]="btnLoading()"
        aria-label="cancel-button"
      ></button>
      @if (!hasPendingRequest()) {
        <button
          pButton
          pRipple
          type="button"
          [label]="'CLIENT_PROFILE.DELETE_CLIENT.DELETE_BUTTON' | translate"
          class="p-button-danger"
          (click)="deleteClient()"
          [loading]="btnLoading()"
          aria-label="delete-client-button"
        ></button>
      }
    </div>
  </ng-template>
</p-dialog>
